import { parseComm100Key } from '@/domain/chat';
import { markChatPanelClosed, scheduleMobileHideRetries } from '../chat-session';
import type { ChatAdapter } from './types';

const PRIMARY = 'https://vue.comm100.com/livechat.ashx?siteId=';
const STANDBY = 'https://standby.comm100vue.com/livechat.ashx?siteId=';
const STANDBY_MS = 5000;

let campaignId: string | null = null;
let mobileMode = false;

function injectComm100Script(src: string): void {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = src;
  const first = document.getElementsByTagName('script')[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
}

function ensureComm100Mount(): void {
  if (document.getElementById('livechat')) return;
  const el = document.createElement('div');
  el.id = 'livechat';
  document.body.appendChild(el);
}

function hideLauncher(): void {
  window.Comm100API?.set('livechat.button.isVisible', false);
}

export const comm100Adapter: ChatAdapter = {
  id: 'comm100',

  async load(ctx) {
    const parsed = parseComm100Key(ctx.key);
    if (!parsed) {
      throw new Error('Invalid Comm100 ChatKey');
    }
    campaignId = parsed.campaignId;
    mobileMode = ctx.isMobile;
    ensureComm100Mount();

    const api: NonNullable<Window['Comm100API']> = window.Comm100API || {
      on() {},
      do() {},
      set() {},
    };
    window.Comm100API = api;

    api.chat_buttons = api.chat_buttons || [];
    api.chat_buttons.push({ code_plan: parsed.campaignId, div_id: 'livechat' });
    api.site_id = parsed.siteId;
    api.main_code_plan = parsed.campaignId;

    injectComm100Script(`${PRIMARY}${parsed.siteId}`);
    window.setTimeout(() => {
      if (!api.loaded) {
        injectComm100Script(`${STANDBY}${parsed.siteId}`);
      }
    }, STANDBY_MS);

    await new Promise<void>((resolve) => {
      const prev = api.onReady;
      api.onReady = () => {
        prev?.();
        if (mobileMode) {
          hideLauncher();
        }
        api.on('livechat.chat.agent.input', () => {
          ctx.onUnread(1);
        });
        api.on('livechat.chat.restore', () => {
          ctx.onUnread(0);
        });
        api.on('livechat.chat.close', () => {
          if (mobileMode) markChatPanelClosed();
        });
        resolve();
      };
      if (api.loaded) {
        api.onReady();
      }
    });

    if (mobileMode) {
      scheduleMobileHideRetries(hideLauncher);
    }
  },

  open() {
    if (!campaignId || !window.Comm100API) return;
    window.Comm100API.do('livechat.button.click', campaignId);
  },

  hideLauncher() {
    hideLauncher();
  },
};
